import { useState } from 'react';
import { Review, categories, getInstagramShortcode, getInstagramThumbnail } from '@/mocks/reviews';
import StarRating from '@/components/base/StarRating';

interface VideoCardProps {
  review: Review;
  onVideoClick: (review: Review) => void;
}

export default function VideoCard({ review, onVideoClick }: VideoCardProps) {
  const [imgError, setImgError] = useState(false);

  const categoryLabel = categories.find(c => c.id === review.category)?.label || review.category;

  const isInstagram = !!review.instagramUrl;
  const instagramShortcode = isInstagram ? getInstagramShortcode(review.instagramUrl!) : null;
  const thumbnailSrc = review.thumbnailUrl || (isInstagram && instagramShortcode
    ? getInstagramThumbnail(instagramShortcode)
    : '');

  return (
    <div className="group flex flex-col bg-background-50 rounded-xl border border-background-200/70 overflow-hidden transition-all duration-300 hover:border-primary-200">
      <button
        onClick={() => onVideoClick(review)}
        className="relative w-full aspect-[9/16] overflow-hidden bg-background-200 cursor-pointer"
        aria-label={`${review.customerName} yorum videosunu oynat`}
      >
        {!imgError ? (
          <img
            src={thumbnailSrc}
            alt={`${review.customerName} - ${review.procedure} yorum videosu`}
            title={`${review.customerName} - ${review.procedure}`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-background-200 to-background-100 gap-3">
            {isInstagram ? (
              <>
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500">
                  <i className="ri-instagram-fill text-white text-2xl"></i>
                </span>
                <span className="text-xs text-foreground-400">Instagram'da İzle</span>
              </>
            ) : (
              <span className="w-16 h-16 flex items-center justify-center">
                <i className="ri-video-line text-foreground-300 text-3xl"></i>
              </span>
            )}
          </div>
        )}
        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300"></div>
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-white/90 group-hover:bg-white transition-all duration-300 group-hover:scale-110 shadow-lg">
            <i className="ri-play-fill text-primary-500 text-2xl md:text-3xl ml-0.5"></i>
          </span>
        </span>
        <div className="absolute top-3 left-3">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium backdrop-blur-sm ${
            isInstagram
              ? 'bg-gradient-to-r from-pink-500/90 to-red-500/90 text-white'
              : 'bg-white/90 text-foreground-800'
          }`}>
            <span className="w-3 h-3 flex items-center justify-center">
              {isInstagram ? (
                <i className="ri-instagram-fill text-[11px]"></i>
              ) : (
                <i className="ri-vidicon-line text-[10px]"></i>
              )}
            </span>
            {isInstagram ? 'Instagram' : 'Video'}
          </span>
        </div>
      </button>

      <div className="flex flex-col gap-2 p-3 md:p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm md:text-base font-semibold text-foreground-900 leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
              {review.customerName}
            </h3>
            <p className="text-xs md:text-sm text-foreground-500 mt-0.5 line-clamp-1">
              {review.procedure}
            </p>
          </div>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-50 text-[10px] md:text-xs font-medium text-primary-700 whitespace-nowrap shrink-0">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-map-pin-line text-[9px]"></i>
            </span>
            {review.location.split(',')[0]}
          </span>
        </div>

        <StarRating rating={review.rating} size="sm" />

        <a
          href={`https://wa.me/905433724578?text=Merhaba%2C%20${encodeURIComponent(review.procedure)}%20işlemi%20hakkında%20bilgi%20almak%20istiyorum.`}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="flex items-center justify-center gap-1.5 w-full mt-1 py-2.5 rounded-lg border border-primary-300 text-primary-600 hover:bg-primary-50 transition-colors duration-200 text-xs md:text-sm font-medium whitespace-nowrap cursor-pointer"
          aria-label={`${review.procedure} hakkında WhatsApp ile bilgi al`}
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-whatsapp-fill text-base"></i>
          </span>
          Bu işlem hakkında bilgi al
        </a>
      </div>
    </div>
  );
}